import React from 'react'
import { observer } from 'mobx-react'
import { Button, Row, Col } from 'react-bootstrap'
import { observable, reaction } from 'mobx'
import {
  i18nTranslate,
  getLiveEventStatus,
  isExpressCheckout,
  convertToBoolean,
  trackGTMEvents,
  enableBillingAddressRequired,
} from 'src/utils'
import {
  checkoutContainer,
  tokenExContainer,
  paymetricContainer,
  storeContainer,
  cartContainer,
} from 'src/models'
import PaymentForm from './PaymentForm'
import { BillingAddressForm } from './BillingAddressForm'
import { parsePhoneNumberField } from 'src/utils/signUpUtils'
import {
  getRestrictedCardType,
  triggerPaymetricValidationOnSubmit,
  normalizePaymetricProvider,
} from 'src/utils/paymentContainerHelper'
import { APPConfig, IS_BROWSER } from 'config/appConfig'
import PaymentInstallment from '../PaymentListWithForm/PaymentInstallment'
import { overlayState, toastState } from 'src/views/components'
import { removeShippingAddressFormValidationMeta } from 'src/views/components/CommonFormForCS/shippingAddressFormValidation'
import './styles.scss'

@observer
class PaymentMarketForm extends React.Component {
  @observable combinedFormData = {
    paymentData: {},
    addressData: {},
  }

  @observable paymentFormRef = React.createRef()
  @observable AddressFormRef = React.createRef()
  @observable paymentFormValid = false
  @observable addressFormValid = false
  @observable enableLiveEvent = getLiveEventStatus()
  constructor(props) {
    super(props)
    this.enableDirectPaymetricAndAccertifyIntegration = convertToBoolean(
      APPConfig?.getAppConfig()?.enableDirectPaymetricAndAccertifyIntegration
    )
  }

  static defaultProps = {
    includePaymetricCvv: true,
  }

  /**
   * Gets the active payment container based on feature flag
   */
  getPaymentContainer = () => {
    return this.enableDirectPaymetricAndAccertifyIntegration
      ? paymetricContainer
      : tokenExContainer
  }

  /**
   * Gets the tokenized form data from the active payment container
   */
  getTokenizedFormData = () => {
    return this.getPaymentContainer()?.tokenExFormData || {}
  }

  /**
   * Gets validation data from the active payment container
   */
  getValidationData = () => {
    return this.getPaymentContainer()?.tokenExValidData || {}
  }

  componentDidMount() {
    const reactions = reaction(
      () => [this.paymentFormValid, this.addressFormValid],
      (values, reaction) => {
        if (values[0] && values[1]) {
          this.makeAPIsIfBothFormsAreValid()
        }
      }
    )
    if (this.enableLiveEvent === 'true') {
      // In Live Event enable shipping address by default for new payment
      if (this.props?.isFromCheckout) {
        if (!this.props?.isPaymentEdit) {
          checkoutContainer.paymentBillingSameAsShippingFlag = false
        }
      }
    }
  }

  componentWillUnmount() {
    if (this._expressSubmitSafetyTimer) {
      clearTimeout(this._expressSubmitSafetyTimer)
      this._expressSubmitSafetyTimer = null
    }
  }
  makeAPIsIfBothFormsAreValid = async () => {
    const {
      onSubmitForm = function () {},
      isFromEditPayment = false,
      isFromEditSubscription = false,
      isPaymentEdit = false,
    } = this.props

    // Validate terms checkbox for live events before proceeding
    const enableLiveEvent = convertToBoolean(getLiveEventStatus())
    const termsCheckbox = document.getElementById('event-submit-terms-checkbox')
    const isTermsChecked = termsCheckbox ? termsCheckbox.checked : true
    if (enableLiveEvent === true && !isTermsChecked && !isPaymentEdit) {
      return false
    }

    let formData = removeShippingAddressFormValidationMeta({
      ...this.combinedFormData.paymentData,
      ...this.combinedFormData.addressData,
    })

    let newTransformedFormData = {}
    const {
      phone = '',
      phoneCountryCode = '',
      zip = '',
      postalCode = '',
      addressLine2 = '',
      city = '',
    } = formData
    let phoneNumber = ''
    if (phone !== '') {
      const phoneObj =
        (await parsePhoneNumberField(phone, phoneCountryCode)) || {}
      phoneNumber = phoneObj?.number || ''
    }
    newTransformedFormData = {
      ...formData,
      billingSameAsShipping: checkoutContainer.paymentBillingSameAsShippingFlag,
      addPaymentToProfile: checkoutContainer.addPaymentToProfile,
      phone: phoneNumber,
      zip: zip || postalCode.toUpperCase(),
      addressLine2: addressLine2 || '',
      city: city || '',
    }
    if (isExpressCheckout()) {
      if (this.paymentFormRef?.current?.values?.addPaymentToProfile) {
        newTransformedFormData = {
          ...newTransformedFormData,
          addPaymentToProfile: true,
        }
      } else {
        newTransformedFormData = {
          ...newTransformedFormData,
          addPaymentToProfile: false,
        }
      }
    }

    const proceedSubmit = () => {
      if (typeof onSubmitForm === 'function') {
        let newFormData =
          !checkoutContainer.paymentBillingSameAsShippingFlag ||
          isFromEditPayment
            ? transformedFormData
            : isPaymentEdit
              ? { paymentMethodType: paymentType, ...newTransformedFormData }
              : newTransformedFormData
        const tokenizedFormData = this.getTokenizedFormData()
        const activeContainer = this.getPaymentContainer()
        const detectedProvider = this
          .enableDirectPaymetricAndAccertifyIntegration
          ? activeContainer?.getCardProviderFromTokenData?.(tokenizedFormData)
          : tokenizedFormData?.cardType
        const tokenizedData = {
          cardNumber: tokenizedFormData?.lastFour,
          provider: normalizePaymetricProvider(detectedProvider),
        }
        // Check if card should be restricted based on GPS or market rules
        const cardType = detectedProvider
        const isRestricted = checkoutContainer.isGPSorDiscoverRestricted(
          normalizePaymetricProvider(cardType)
        )

        if (isRestricted) {
          this.resetExpressSubmitState()
          if (this.enableDirectPaymetricAndAccertifyIntegration) {
            console.log(
              '[PAYMETRIC_DEBUG] [Payment] Restricted card detected - triggering session refresh'
            )
            const restrictedCardType = getRestrictedCardType(
              normalizePaymetricProvider(cardType)
            )
            if (restrictedCardType) {
              toastState.setToastMessage(
                i18nTranslate(
                  restrictedCardType.key,
                  restrictedCardType.fallback
                ),
                false
              )
            }
            paymetricContainer.retryPaymetricSession()
          }
          return
        }

        formData?.setToDefaultPayment &&
          trackGTMEvents({
            event: 'paymentSetAsDefault',
            context: 'PROFILE',
            paymentType: tokenizedFormData?.cardType?.toUpperCase() || '',
            method: 'addPaymentLink',
          })
        onSubmitForm({
          ...newFormData,
          ...(isFromEditPayment ? {} : tokenizedData),
          ...(isFromEditSubscription ? tokenizedData : {}),
        })
      }
    }
    const {
      stateList = '',
      wardList = '',
      districtList = '',
      state = '',
      district = '',
      county = '',
      ...remainingFormData
    } = newTransformedFormData
    const stateValue = stateList || state
    const cityValue = wardList || city
    const districtValue = county || districtList || district
    const paymentType = 'CREDITCARD'
    const transformedFormData = {
      ...remainingFormData,
      state: stateValue,
      county: districtValue,
      city: cityValue,
      paymentMethodType: paymentType,
    }

    {
      isFromEditPayment
        ? proceedSubmit()
        : this.getPaymentContainer().tokenize(proceedSubmit)
    }
  }

  scrollToPaymentForm = () => {
    if (!this.enableDirectPaymetricAndAccertifyIntegration) return
    const section =
      document.querySelector('[data-testid="qa-add-payment-container"]') ||
      document.querySelector('[data-testid="qa-edit-payment-container"]')
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  resetExpressSubmitState = () => {
    if (!isExpressCheckout()) {
      return
    }
    checkoutContainer.isPaymentApiInProgress = false
    checkoutContainer.isToDisableSubmitOrderButton = false
    checkoutContainer.activeSubmitOrderButton = true
    overlayState.hideLoader()
  }

  handleSubmit = async () => {
    if (this.enableDirectPaymetricAndAccertifyIntegration) {
      triggerPaymetricValidationOnSubmit({ paymetricContainer })
    }
    if (this.paymentFormRef?.current?.values?.addPaymentToProfile) {
      trackGTMEvents({
        event: 'paymentUsedPreviouslySaved',
        eventType: 'Payment used for Order saved to Profile',
      })
    }
    this.paymentFormValid = false
    this.addressFormValid = false
    // submitForm() awaits validation + onSubmit; handleSubmit() fires-and-forgets.
    await this.paymentFormRef.current.submitForm()
    const hasYupErrors =
      Object.keys(this.paymentFormRef?.current?.errors || {}).length > 0
    if (hasYupErrors) {
      // Express Checkout disables Submit Order via SubmitOrder.checkSubmitGuards
      // on click; on silent validation failure nothing else re-enables it.
      if (isExpressCheckout()) {
        this.resetExpressSubmitState()
      }
      if (this.enableDirectPaymetricAndAccertifyIntegration) {
        this.scrollToPaymentForm()
      }
    }
    const isBillingAddressRequired = enableBillingAddressRequired()
    if (isBillingAddressRequired) {
      !checkoutContainer.paymentBillingSameAsShippingFlag &&
        (await this.AddressFormRef.current.handleSubmit())
    } else {
      this.addressFormValid = true
    }
    /** Safety net for silent tokenize failures (e.g. paymetric $XIFrame.submit
     * rejects an empty card number without invoking proceedSubmit). Fires
     * after a delay only if no legitimate payment flow is in progress; keeps
     * the Express Checkout Submit Order button from getting stuck disabled. */
    if (isExpressCheckout()) {
      clearTimeout(this._expressSubmitSafetyTimer)
      this._expressSubmitSafetyTimer = setTimeout(() => {
        if (
          !checkoutContainer.isPaymentApiInProgress &&
          !checkoutContainer.expressPaymentAdded &&
          !checkoutContainer.autoPaymentSuccess &&
          !checkoutContainer.activeSubmitOrderButton
        ) {
          this.resetExpressSubmitState()
        }
      }, 2500)
    }
  }

  handlePaymentSubmit = data => {
    // For TokenEx, validate CVV and card synchronously before proceeding.
    // For Paymetric, tokenize() calls $XIFrame.submit() which is the source
    // of truth — validation is handled there, not here.
    if (!this.enableDirectPaymetricAndAccertifyIntegration) {
      const validationData = this.getValidationData()
      if (!validationData?.isCvvValid || !validationData?.isValid) {
        this.resetExpressSubmitState()
        return false
      }

      // TokenEx has card type available before tokenization, so pre-check it.
      // Paymetric gets card type after tokenize(), and is checked in proceedSubmit.
      const cardType = validationData?.cardType
      const isRestricted = checkoutContainer.isGPSorDiscoverRestricted(cardType)
      if (isRestricted) {
        this.resetExpressSubmitState()
        return false
      }
    }

    if (
      isExpressCheckout() &&
      !this.enableDirectPaymetricAndAccertifyIntegration
    ) {
      overlayState.showLoader()
      const billingAddressErrorCheckIntiCount = 1
      const billingAddressErrorCheckMaxCount = 10
      let addressFormRefErrorCheck = billingAddressErrorCheckIntiCount
      const checkAddressFormRefError = () => {
        setTimeout(() => {
          if (
            this.AddressFormRef?.current?.errors &&
            Object.keys(this.AddressFormRef?.current?.errors).length > 0
          ) {
            overlayState.hideLoader()
          } else if (
            addressFormRefErrorCheck <= billingAddressErrorCheckMaxCount
          ) {
            addressFormRefErrorCheck = addressFormRefErrorCheck + 1
            checkAddressFormRefError()
          }
        }, 700)
      }
      checkAddressFormRefError()
    }
    this.combinedFormData = { ...this.combinedFormData, paymentData: data }
    const {
      isFromEditPayment = false,
      paymentData = {},
      isPaymentEdit = false,
      billingAddress = {},
    } = this.props
    const isBillingAddressRequired = enableBillingAddressRequired()
    if (
      isPaymentEdit &&
      isBillingAddressRequired &&
      Object.keys(billingAddress).length > 0
    ) {
      this.combinedFormData = {
        ...this.combinedFormData,
        addressData: billingAddress,
      }
    }
    this.paymentFormValid = true
    this.addressFormValid = checkoutContainer.paymentBillingSameAsShippingFlag
      ? true
      : false
    if (
      checkoutContainer.paymentBillingSameAsShippingFlag &&
      isFromEditPayment
    ) {
      this.combinedFormData = {
        ...this.combinedFormData,
        addressData: paymentData?.billingAddress,
      }
    }
  }

  handleAddressSubmit = data => {
    this.combinedFormData = { ...this.combinedFormData, addressData: data }
    this.addressFormValid = true
  }

  handleCancelButton = () => {
    const { onCancel = function () {} } = this.props
    if (typeof onCancel === 'function') {
      onCancel()
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
    }
  }

  renderSaveChangesButton = () => {
    return (
      <Button
        variant="primary"
        type="submit"
        form="payment-form"
        className="button-style"
        data-testid="qa-save-changes"
        onClick={() => {
          this.handleSubmit()
        }}>
        {i18nTranslate('checkout.saveChanges', 'Save Changes')}
      </Button>
    )
  }

  renderContinueButton = () => {
    return (
      <Row className="d-flex w-100 mt-3 continue-btn-wrapper" noGutters>
        <Col className="new-payment-cancel-btn-wrapper pr-1">
          <Button
            variant="primary"
            type="submit"
            form="payment-form"
            className="button-style express-action-d-none"
            data-testid="qa-add-new-pay-save"
            id="add-new-pay-save"
            disabled={checkoutContainer.isPaymentApiInProgress}
            onClick={event => {
              // CX121-3102 - to prevent multiple clicks
              // https://stackoverflow.com/a/42664936
              if (!event.detail || event.detail == 1) {
                this.handleSubmit()
              }
            }}>
            {i18nTranslate('checkout.save', 'Save')}
          </Button>
        </Col>
        <Col className="pl-1 new-payment-cancel-btn-wrapper">
          <Button
            id="new-payment-cancel-btn-checkout"
            variant="outline-primary"
            type="submit"
            form="payment-form"
            className="button-style express-action-d-none"
            data-testid="qa-add-new-pay-cancel"
            onClick={this.props?.cancelPaymentFlag}>
            {i18nTranslate('checkout.cancel', 'Cancel')}
          </Button>
        </Col>
      </Row>
    )
  }

  renderPaymentSubmitButton = () => {
    const { isFromEditPayment = false } = this.props
    return (
      <Row className="edit-payment-button-container-row w-100" noGutters>
        <Col className="edit-payment-button-container-first-col d-flex align-items-stretch">
          <div className="w-100 h-100 d-flex align-items-stretch">
            {isFromEditPayment
              ? this.renderEditPaymentSubmitButton()
              : this.renderAddPaymentSubmitButton()}
          </div>
        </Col>
        <Col className="edit-payment-button-container-last-col d-flex align-items-stretch">
          <div className="w-100 h-100 d-flex align-items-stretch">
            {this.renderCancelButton()}
          </div>
        </Col>
      </Row>
    )
  }

  renderAddPaymentSubmitButton = () => {
    return (
      <Button
        variant="primary"
        type="submit"
        form="payment-form"
        className="button-style payment-add-button add-button"
        data-testid="qa-add-card-submit"
        onClick={() => {
          this.handleSubmit()
        }}>
        {i18nTranslate('payment.submit', 'Add Payment')}
      </Button>
    )
  }

  renderEditPaymentSubmitButton = () => {
    return (
      <Button
        variant="primary"
        type="submit"
        form="payment-form"
        className="button-style"
        data-testid="qa-save-changes"
        onClick={() => {
          this.handleSubmit()
        }}>
        {i18nTranslate('payment.saveChanges', 'Save')}
      </Button>
    )
  }

  renderCancelButton = () => {
    return (
      <Button
        id="new-payment-cancel-btn-profile"
        variant="outline-primary"
        className="button-style"
        onClick={this.handleCancelButton}
        data-testid="qa-cancel-payment">
        {i18nTranslate('payment.cancel', 'Cancel')}
      </Button>
    )
  }

  renderSubmitButton = () => {
    const { isFromPayment = false, isFromEditSubscription = false } = this.props
    const continueButton = isFromEditSubscription
      ? this.renderSaveChangesButton()
      : this.renderContinueButton()

    const submitButtonView = isFromPayment ? (
      this.renderPaymentSubmitButton()
    ) : isFromEditSubscription ? (
      <Row className="edit-payment-button-container-row w-100" noGutters>
        <Col>{continueButton}</Col>
      </Row>
    ) : (
      continueButton
    )

    return (
      <Row noGutters className="justify-content-end">
        {submitButtonView}
      </Row>
    )
  }

  render() {
    const {
      paymentData = {},
      isPaymentEdit = false,
      isFromCheckout = false,
      formType = false,
      billingAddressType = '',
      billingAddress = {},
    } = this.props
    let className = formType ? formType : 'payment-market-form'

    if (this.enableLiveEvent === 'true') {
      if (isFromCheckout && !isPaymentEdit) {
        className += ' live-event-enabled'
      }
    }

    const isNewPaymentFomEventCheckout =
      this.enableLiveEvent === 'true' && isFromCheckout && !isPaymentEdit
    const isIstallmentEnabled = convertToBoolean(
      APPConfig.getAppConfig()?.enableCheckoutInstallment
    )
    const isSubscriptionItemInCart = cartContainer.hasSubscriptionProduct()
    const istoEnableInstallmet =
      billingAddressType === 'checkout' &&
      isIstallmentEnabled &&
      !isSubscriptionItemInCart
    const isBillingAddressRequired = enableBillingAddressRequired()
    return (
      <div className={className}>
        {/*Payment Form */}
        <PaymentForm
          onSubmit={this.handlePaymentSubmit}
          refId={this.paymentFormRef}
          hideDefaultSubPaymentCheckbox={
            this.props.hideDefaultSubPaymentCheckbox || false
          }
          billingAddress={billingAddress}
          includePaymetricCvv={this.props.includePaymetricCvv}
          {...this.props}
        />
        {isBillingAddressRequired &&
          (!checkoutContainer.paymentBillingSameAsShippingFlag ||
            isNewPaymentFomEventCheckout) &&
          IS_BROWSER && (
            <>
              {/*Address Form */}
              <BillingAddressForm
                onSubmit={this.handleAddressSubmit}
                refId={this.AddressFormRef}
                isFromSubscriptionPayment={isPaymentEdit}
                addressInfo={paymentData?.billingAddress}
                type="payment"
                isFromCheckout={isFromCheckout}
                billingAddressType={billingAddressType}
                selectedMarket={checkoutContainer.selectedMarket}
                defaultMarket={storeContainer.defaultShippingRegion}
              />
            </>
          )}
        {istoEnableInstallmet && <PaymentInstallment />}
        {this.renderSubmitButton()}
      </div>
    )
  }
}

export default PaymentMarketForm
export { PaymentMarketForm }
